// ============================================================
// MANIC MEETER — Types & Interfaces (Browser Edition)
// ============================================================

export enum Department {
  Frontend = "Frontend",
  Backend = "Backend",
  Client = "Client",
  HR = "HR",
  Marketing = "Marketing",
  Management = "Management",
}

export enum ColleagueRole {
  FrontendDev = "Frontend-Entwickler",
  BackendDev = "Backend-Entwickler",
  ClientManager = "Client Manager",
  HRManager = "HR-Manager",
  MarketingGuru = "Marketing-Guru",
  Chef = "Chef",
  Abteilungsleiter = "Abteilungsleiter",
  Teamleiter = "Teamleiter",
  Designer = "UX-Designer",
  Praktikant = "Praktikant",
  ScrumMaster = "Scrum Master",
  ProductOwner = "Product Owner",
  /**
   * The colleague who does not speak the language. Enum value is an opaque,
   * persisted id — his displayed name and role come from the active locale,
   * and flip with it.
   */
  Sprachbarriere = "Sprachbarriere",
}

export enum PlayerRole {
  Praktikant = "Praktikant",
  Azubi = "Azubi",
  Werkstudent = "Werkstudent",
  JuniorDev = "Junior-Dev",
}

// Higher = addressed more often = harder. 1–4 stars.
export const PLAYER_ROLE_DIFFICULTY: Record<PlayerRole, number> = {
  [PlayerRole.Praktikant]: 0.08,
  [PlayerRole.Azubi]: 0.15,
  [PlayerRole.Werkstudent]: 0.22,
  [PlayerRole.JuniorDev]: 0.30,
};

export const PLAYER_ROLE_STARS: Record<PlayerRole, number> = {
  [PlayerRole.Praktikant]: 1,
  [PlayerRole.Azubi]: 2,
  [PlayerRole.Werkstudent]: 3,
  [PlayerRole.JuniorDev]: 4,
};

export enum MeetingType {
  DailyStandup = "Daily Standup",
  Planning = "Sprint Planning",
  LunchMeeting = "Lunch-Meeting",
  OneOnOne = "1-on-1",
  Scrum = "Scrum-Meeting",
  Schulung = "Schulung",
  Retro = "Retrospektive",
  AllHands = "All-Hands",
  Brainstorming = "Brainstorming-Session",
  StatusUpdate = "Status Update",
  Vorstandsmeeting = "Vorstandsmeeting",
}

export interface Colleague {
  name: string;
  role: ColleagueRole;
  department: Department;
  talkiness: number;
  catchPhrase: string;
  isCurrentlySpeaking: boolean;
}

export interface Player {
  name: string;
  role: PlayerRole;
  salary: number;
  fatigue: number;
  maxFatigue: number;
  reputation: number;
  meetingsSurvived: number;
  daysSurvived: number;
}

// 1 round = every participant speaks once (in turnOrder).
// roundsRemaining counts full rounds left.
export interface Meeting {
  type: MeetingType;
  participants: Colleague[];
  roundsTotal: number;
  roundsRemaining: number;
  currentSpeakerIdx: number;   // index into turnOrder for current speaker
  turnOrder: number[];         // values: 0..N-1 = colleague indices, playerSlot = player
  playerSlot: number;          // the magic number in turnOrder representing the player
  directionForward: boolean;
  day: number;
  isOnline: boolean;
}

export enum ActionType {
  Procrastinate = "procrastinate",
  Action = "action",
  Timeout = "timeout",
}

export enum SpecialEventType {
  Question = "question",
  Brainstorming = "brainstorming",
  SimonSays = "simonSays",
  BuzzwordBingo = "buzzwordBingo",
  EmailSortieren = "emailSortieren",
  KalenderTetris = "kalenderTetris",
  CodeReview = "codeReview",
  KiHalluzination = "kiHalluzination",
  None = "none",
}

export interface Question {
  prompt: string;
  answers: string[];
  correctIndex: number;
  wrongPenaltyRounds: number;
}

export interface BrainstormingChallenge {
  word: string;
  timeMs: number;
  context: string;
}

export interface SimonSaysChallenge {
  sequence: string[];
  timeMs: number;
  context: string;
}

export interface BuzzwordBingoChallenge {
  words: string[];       // 4 buzzwords shown as a "bingo card"
  correctOrder: number[]; // indices in the order they are "called"
  timeMs: number;
  context: string;
}

export interface EmailSortierenChallenge {
  emails: { subject: string; correct: "spam" | "wichtig" | "delegieren" }[];
  timeMs: number;
  context: string;
}

export interface KalenderTetrisChallenge {
  participants: { name: string; slots: boolean[] }[]; // true = busy, false = free
  timeSlots: string[];       // e.g. ["09:00","10:00","11:00","14:00"]
  correctSlot: number;       // index of the timeslot where ALL participants are free
  timeMs: number;
  context: string;
}

export interface CodeReviewChallenge {
  code: string;              // code snippet with a bug
  options: string[];         // 3 possible fixes
  correctIndex: number;      // which option is correct
  language: string;          // display label e.g. "TypeScript", "SQL", etc.
  timeMs: number;
  context: string;
}

/**
 * The AI wrote the slide. Most of it is even true.
 *
 * `hallucinations` holds the indices of the invented lines — always at least
 * one, never all of them: a slide with nothing true on it is not a puzzle, it
 * is a resignation letter.
 */
export interface KiHalluzinationChallenge {
  tool: string;              // which assistant produced this, e.g. "MeetGPT-5"
  task: string;              // the prompt somebody typed to get it
  statements: string[];      // the output, one claim per line
  hallucinations: number[];  // indices of the invented claims
  timeMs: number;
  context: string;
}

export interface TurnResult {
  message: string;
  fatigueDelta: number;
  salaryDelta: number;
  extraRounds: number;
  fellAsleep: boolean;
  caughtProcrastinating: boolean;
  directionChanged: boolean;
  speakerName?: string;
  speakerRole?: string;
}

export interface DayStats {
  meetingsSurvived: number;
  timesCaught: number;
  timesFellAsleep: number;
  minigamesWon: number;
  minigamesLost: number;
  salaryStart: number;
  salaryEnd: number;
  meetingsSkipped: number;
  monologues: number;
  meetingTypesThisDay: string[];
  // Bingo tracking per meeting
  bingoMeetingSurvived: boolean;
  bingoNoSleep: boolean;
  bingoNoCatch: boolean;
  bingoMinigameWon: boolean;
  bingoMonologue: boolean;
  bingoInvisible: boolean;
  bingoQuestionCorrect: boolean;
  bingoProcrastinateCount: number;
  bingoDirectionChange: boolean;
}

export interface GameState {
  player: Player;
  currentMeeting: Meeting | null;
  meetingQueue: MeetingType[];
  currentDay: number;
  isGameOver: boolean;
  gameOverReason: string;
  log: string[];
  meta: MetaProgression;
  activePowerups: ActivePowerup[];
  consecutiveCaught: number;
  dayStats: DayStats;
  activePerks: string[];
  daysSinceLastEncounter: number;
  attestDaysRemaining: number;
  // Achievement & stats tracking
  achievements: Set<AchievementId>;
  achievementTracker: AchievementTracker;
  sessionStats: SessionStats;
  // Colleague relationships
  relationships: Record<string, ColleagueRelationship>;
  // Meeting bingo
  bingoCard: BingoCard | null;
  // Weekend tracking
  isWeekendEvent: boolean;
  weekendEvent: WeekendEventDef | null;
  // Ending
  ending: EndingType | null;
  // Sound enabled
  soundEnabled: boolean;
  // Peak salary tracking
  peakSalary: number;
}

// ---- Meta-Progression (Impostor Points) ----

export enum StatUpgradeId {
  Gehalt = "gehalt",
  MaxMuedigkeit = "maxMuedigkeit",
  Unauffaelligkeit = "unauffaelligkeit",
  CriticalHit = "criticalHit",
  StandingDesk = "standingDesk",
  VPN = "vpn",
  Ohrstoepsel = "ohrstoepsel",
  Kreditkarte = "kreditkarte",
}

export enum PowerupId {
  Kaffee = "kaffee",
  HandyAkku = "handyAkku",
  NoiseCancelling = "noiseCancelling",
  FakeNotizen = "fakeNotizen",
  LaptopTarnung = "laptopTarnung",
  PizzaBestellen = "pizzaBestellen",
  MuteButton = "muteButton",
  RemoteJoker = "remoteJoker",
  BullshitBingo = "bullshitBingo",
  PromptBibliothek = "promptBibliothek",
  KiZusammenfassung = "kiZusammenfassung",
  KiAgent = "kiAgent",
}

export interface StatUpgrade {
  id: StatUpgradeId;
  name: string;
  description: string;
  maxLevel: number;
  baseCost: number;
  costScaling: number; // cost = baseCost + level * costScaling
  effectPerLevel: number;
}

export interface PowerupDef {
  id: PowerupId;
  name: string;
  description: string;
  cost: number;
  usableInPrep: boolean;   // can be activated during prep phase
  usableInMeeting: boolean; // can be activated mid-meeting
}

export interface ActivePowerup {
  id: PowerupId;
  remainingTurns: number; // -1 = whole meeting, 0 = instant
}

export interface MetaProgression {
  impostorPoints: number;
  totalPointsEarned: number;
  statLevels: Record<StatUpgradeId, number>;
  powerupInventory: Record<PowerupId, number>;
  permanentUnlocks: Record<PermanentUnlockId, boolean>;
  totalBullshitBingosUsed: number;
  endingsUnlocked: EndingType[];
}

// ---- Stat Upgrade Definitions ----

export interface StatUpgradeSpec {
  id: StatUpgradeId;
  maxLevel: number;
  baseCost: number;
  costScaling: number; // cost = baseCost + level * costScaling
  effectPerLevel: number;
}

export const STAT_UPGRADE_SPECS: StatUpgradeSpec[] = [
  { id: StatUpgradeId.Gehalt, maxLevel: 5, baseCost: 3, costScaling: 2, effectPerLevel: 200 },
  { id: StatUpgradeId.MaxMuedigkeit, maxLevel: 5, baseCost: 3, costScaling: 2, effectPerLevel: 10 },
  { id: StatUpgradeId.Unauffaelligkeit, maxLevel: 5, baseCost: 4, costScaling: 3, effectPerLevel: 0.10 },
  { id: StatUpgradeId.CriticalHit, maxLevel: 5, baseCost: 5, costScaling: 3, effectPerLevel: 0.10 },
  { id: StatUpgradeId.StandingDesk, maxLevel: 3, baseCost: 4, costScaling: 3, effectPerLevel: 1 },
  { id: StatUpgradeId.VPN, maxLevel: 3, baseCost: 6, costScaling: 4, effectPerLevel: 0.05 },
  { id: StatUpgradeId.Ohrstoepsel, maxLevel: 3, baseCost: 4, costScaling: 3, effectPerLevel: 1 },
  { id: StatUpgradeId.Kreditkarte, maxLevel: 3, baseCost: 5, costScaling: 4, effectPerLevel: 0.10 },
];

// ---- Powerup Definitions ----

export interface PowerupSpec {
  id: PowerupId;
  cost: number;
  usableInPrep: boolean;    // can be activated during prep phase
  usableInMeeting: boolean; // can be activated mid-meeting
}

export const POWERUP_SPECS: PowerupSpec[] = [
  { id: PowerupId.Kaffee, cost: 2, usableInPrep: true, usableInMeeting: true },
  { id: PowerupId.HandyAkku, cost: 3, usableInPrep: false, usableInMeeting: true },
  { id: PowerupId.NoiseCancelling, cost: 4, usableInPrep: true, usableInMeeting: false },
  { id: PowerupId.FakeNotizen, cost: 3, usableInPrep: false, usableInMeeting: true },
  { id: PowerupId.LaptopTarnung, cost: 5, usableInPrep: false, usableInMeeting: true },
  { id: PowerupId.PizzaBestellen, cost: 8, usableInPrep: true, usableInMeeting: false },
  { id: PowerupId.MuteButton, cost: 6, usableInPrep: true, usableInMeeting: false },
  { id: PowerupId.RemoteJoker, cost: 4, usableInPrep: true, usableInMeeting: false },
  { id: PowerupId.BullshitBingo, cost: 3, usableInPrep: true, usableInMeeting: false },
  // The new way of working, sold by the unit
  { id: PowerupId.PromptBibliothek, cost: 4, usableInPrep: true, usableInMeeting: false },
  { id: PowerupId.KiZusammenfassung, cost: 5, usableInPrep: true, usableInMeeting: false },
  { id: PowerupId.KiAgent, cost: 6, usableInPrep: true, usableInMeeting: false },
];

// Points earned per completed day
export const IMPOSTOR_POINTS_PER_DAY = 1;
// Bonus point for surviving a full day without falling asleep
export const IMPOSTOR_POINTS_BONUS_NO_SLEEP = 1;

export const MIN_WAGE = 1000;
export const STARTING_SALARY = 3200;
export const MAX_FATIGUE = 100;
export const SLEEP_SALARY_PENALTY = 200;
export const CAUGHT_SALARY_PENALTY = 50;
export const TURN_TIMEOUT_MS = 8000;
export const PROCRASTINATE_FATIGUE_REDUCTION = 18;
export const WRONG_ANSWER_EXTRA_ROUNDS = 1;
export const MONOLOGUE_EXTRA_ROUNDS = 1;
export const ONLINE_ADDRESS_MODIFIER = 0.4;
export const FATIGUE_REDUCTION_CORRECT_ANSWER = 8;
export const MEETING_COMPLETION_FATIGUE_REDUCTION = 12;
export const DAY_CHANGE_FATIGUE_RESTORE_PERCENT = 0.85;

// Browser game-loop pacing (ms)
export const DELAY_COLLEAGUE_SPEAK = 1000;
export const DELAY_BETWEEN_ROUNDS = 800;
export const DELAY_RESULT_SHOW = 1000;
export const DELAY_MEETING_INTRO = 1500;

// ---- Daily Perks (run-lasting modifiers) ----

export interface DailyPerk {
  id: string;
  name: string;
  description: string;
  emoji: string;
  isPositive: boolean;
}

export interface DailyPerkSpec {
  id: string;
  emoji: string;
  isPositive: boolean;
}

export const DAILY_PERK_SPECS: DailyPerkSpec[] = [
  // Positive
  { id: "guterSchlaf", emoji: "\u{1F634}", isPositive: true },
  { id: "motivationsschub", emoji: "\u{1F4AA}", isPositive: true },
  { id: "fluesterModus", emoji: "\u{1F92B}", isPositive: true },
  { id: "sparsamkeit", emoji: "\u{1F416}", isPositive: true },
  { id: "zuckerflash", emoji: "\u{1F36C}", isPositive: true },
  { id: "networking", emoji: "\u{1F91D}", isPositive: true },
  { id: "flowState", emoji: "\u{1F9E0}", isPositive: true },
  { id: "doppelterEspresso", emoji: "\u2615", isPositive: true },
  { id: "chefImUrlaub", emoji: "\u{1F3D6}\uFE0F", isPositive: true },
  { id: "fruehstuecksbuffet", emoji: "\u{1F950}", isPositive: true },
  { id: "glueckssocken", emoji: "\u{1F9E6}", isPositive: true },
  { id: "parkplatzGefunden", emoji: "\u{1F17F}\uFE0F", isPositive: true },
  { id: "kiCopilot", emoji: "\u{1F916}", isPositive: true },
  { id: "promptGefunden", emoji: "\u{1FA84}", isPositive: true },
  { id: "agentLaeuft", emoji: "\u2699\uFE0F", isPositive: true },
  { id: "modellUpdate", emoji: "\u2728", isPositive: true },
  // Negative
  { id: "langeGezockt", emoji: "\u{1F3AE}", isPositive: false },
  { id: "kater", emoji: "\u{1F37A}", isPositive: false },
  { id: "schlechtGeschlafen", emoji: "\u{1F635}", isPositive: false },
  { id: "auffaellig", emoji: "\u{1F454}", isPositive: false },
  { id: "mieterhoehung", emoji: "\u{1F3E0}", isPositive: false },
  { id: "sodbrennen", emoji: "\u{1F525}", isPositive: false },
  { id: "ablenkung", emoji: "\u{1F4F1}", isPositive: false },
  { id: "montagsblues", emoji: "\u{1F629}", isPositive: false },
  { id: "stau", emoji: "\u{1F697}", isPositive: false },
  { id: "tastaturDefekt", emoji: "\u2328\uFE0F", isPositive: false },
  { id: "kantineGeschlossen", emoji: "\u{1F6AB}", isPositive: false },
  { id: "druckerStau", emoji: "\u{1F5A8}\uFE0F", isPositive: false },
  { id: "windowsUpdate", emoji: "\u{1FA9F}", isPositive: false },
  { id: "kiAusfall", emoji: "\u{1F50C}", isPositive: false },
  { id: "halluziniertesProtokoll", emoji: "\u{1F300}", isPositive: false },
  { id: "kontextfensterVoll", emoji: "\u{1F4DC}", isPositive: false },
  { id: "promptInjection", emoji: "\u{1F489}", isPositive: false },
];

// ---- Perk Modifier Aggregation ----

export interface PerkModifiers {
  fatigueGainMult: number;
  fatigueReductionMult: number;
  addressChanceMult: number;
  salaryPenaltyMult: number;
  minigameTimeMult: number;
  salaryPerDay: number;
  dayRecoveryMult: number;
}

// ---- Random Encounters ----

export interface EncounterEffect {
  fatigue?: number;
  salary?: number;
  addRandomPositivePerk?: boolean;
  addRandomNegativePerk?: boolean;
  removeNegativePerk?: boolean;
  dropRandomPowerup?: boolean;
}

export interface EncounterChoice {
  label: string;
  message: string;
  effects: EncounterEffect;
  alternateMessage?: string;
  alternateEffects?: EncounterEffect;
  alternateChance?: number;
}

export interface RandomEncounter {
  id: string;
  name: string;
  emoji: string;
  intro: string;
  choices: EncounterChoice[];
}

// ---- Achievements ----

export enum AchievementId {
  MeetingMarathon = "meetingMarathon",
  Unsichtbar = "unsichtbar",
  Schlafwandler = "schlafwandler",
  BullshitBingo = "bullshitBingo",
  Unantastbar = "unantastbar",
  MonologKoenig = "monologKoenig",
  Speedrunner = "speedrunner",
  Gehaltsverhandler = "gehaltsverhandler",
  Fluesterer = "fluesterer",
  Legend = "legend",
  KoffeinJunkie = "koffeinJunkie",
  TeflonBeschichtung = "teflonBeschichtung",
  Kartenhai = "kartenhai",
  MinigameMeister = "minigameMeister",
  Ueberlebenskuenstler = "ueberlebenskuenstler",
}

export interface AchievementDef {
  id: AchievementId;
  name: string;
  description: string;
  emoji: string;
}

export interface AchievementSpec {
  id: AchievementId;
  emoji: string;
}

export const ACHIEVEMENT_SPECS: AchievementSpec[] = [
  { id: AchievementId.MeetingMarathon, emoji: "\u{1F3C3}" },
  { id: AchievementId.Unsichtbar, emoji: "\u{1F47B}" },
  { id: AchievementId.Schlafwandler, emoji: "\u{1F634}" },
  { id: AchievementId.BullshitBingo, emoji: "\u{1F4CA}" },
  { id: AchievementId.Unantastbar, emoji: "\u{1F977}" },
  { id: AchievementId.MonologKoenig, emoji: "\u{1F3A4}" },
  { id: AchievementId.Speedrunner, emoji: "\u26A1" },
  { id: AchievementId.Gehaltsverhandler, emoji: "\u{1F4B0}" },
  { id: AchievementId.Fluesterer, emoji: "\u{1F92B}" },
  { id: AchievementId.Legend, emoji: "\u{1F3C6}" },
  { id: AchievementId.KoffeinJunkie, emoji: "\u2615" },
  { id: AchievementId.TeflonBeschichtung, emoji: "\u{1F6E1}\uFE0F" },
  { id: AchievementId.Kartenhai, emoji: "\u{1F0CF}" },
  { id: AchievementId.MinigameMeister, emoji: "\u{1F3AE}" },
  { id: AchievementId.Ueberlebenskuenstler, emoji: "\u{1F4B8}" },
];

// ---- Session Stats (tracked during gameplay, saved to DB) ----

export interface SessionStats {
  // Cumulative across all runs
  totalMeetingsSurvived: number;
  totalDaysSurvived: number;
  totalTimesCaught: number;
  totalTimesFellAsleep: number;
  totalMinigamesWon: number;
  totalMinigamesLost: number;
  totalMonologues: number;
  totalProcrastinations: number;
  totalSuccessfulProcrastinations: number;
  totalKaffeeUsed: number;
  totalCardDrawWins: number;
  totalBossMeetings: number;
  // Per meeting type: how often survived, how often failed (fell asleep/caught)
  meetingTypeStats: Record<string, { survived: number; fellAsleep: number; caught: number }>;
  // Colleague role stats: fatigue caused
  colleagueRoleStats: Record<string, { fatigueCaused: number; encounters: number }>;
  // Best run records
  bestDaysSurvived: number;
  bestEndingSalary: number;
  longestMeetingSurvived: number; // by participant count * rounds
}

// ---- Achievement Tracking (per-session counters) ----

export interface AchievementTracker {
  // Unantastbar: consecutive successful procrastinations
  consecutiveSuccessfulProcs: number;
  // Flüsterer: meetings where player only spoke when addressed
  consecutiveSilentMeetings: number;
  spokeUnpromptedThisMeeting: boolean;
  // Speedrunner: all questions correct this meeting, count
  allCorrectThisMeeting: boolean;
  questionsThisMeeting: number;
  // Monolog-König: monologues today
  monologuesToday: number;
  // Unsichtbar: was addressed this meeting
  wasAddressedThisMeeting: boolean;
  // BullshitBingo: max buzzwords in one meeting
  maxBuzzwordsInMeeting: number;
}

// ---- Vendor Encounters (before boss meetings) ----

export interface VendorItem {
  powerupId: PowerupId;
  salaryCost: number;
  label: string;
}

export interface VendorEncounter {
  id: string;
  name: string;
  emoji: string;
  intro: string;
  items: VendorItem[];
}

export interface VendorItemSpec {
  powerupId: PowerupId;
  salaryCost: number;
}

export interface VendorSpec {
  id: string;
  emoji: string;
  items: VendorItemSpec[];
}

export const VENDOR_SPECS: VendorSpec[] = [
  {
    id: "baeckerauto",
    emoji: "\u{1F950}",
    items: [
      { powerupId: PowerupId.Kaffee, salaryCost: 80 },
      { powerupId: PowerupId.NoiseCancelling, salaryCost: 150 },
      { powerupId: PowerupId.MuteButton, salaryCost: 200 },
      { powerupId: PowerupId.FakeNotizen, salaryCost: 100 },
    ],
  },
  {
    id: "hardwarelieferant",
    emoji: "\u{1F4E6}",
    items: [
      { powerupId: PowerupId.RemoteJoker, salaryCost: 120 },
      { powerupId: PowerupId.LaptopTarnung, salaryCost: 180 },
      { powerupId: PowerupId.HandyAkku, salaryCost: 90 },
      { powerupId: PowerupId.PizzaBestellen, salaryCost: 250 },
    ],
  },
  {
    id: "kaffeedealer",
    emoji: "\u2615",
    items: [
      { powerupId: PowerupId.Kaffee, salaryCost: 60 },
      { powerupId: PowerupId.BullshitBingo, salaryCost: 100 },
      { powerupId: PowerupId.NoiseCancelling, salaryCost: 130 },
    ],
  },
];

// ---- Colleague Relationships ----

export interface ColleagueRelationship {
  /** Positive = friendly, negative = hostile. Range: -10 to 10 */
  affinity: number;
  /** Total interactions */
  encounters: number;
}

// ---- Meeting Bingo ----

export interface BingoGoal {
  id: string;
  description: string;
  emoji: string;
  check: "meetingSurvived" | "noSleep" | "noCatch" | "minigameWon" | "monologue" | "invisible" | "questionCorrect" | "procrastinated3x" | "directionChange";
}

export interface BingoGoalSpec {
  id: string;
  emoji: string;
  check: BingoGoal["check"];
}

export const BINGO_GOAL_SPECS: BingoGoalSpec[] = [
  { id: "survive", emoji: "\u2705", check: "meetingSurvived" },
  { id: "noSleep", emoji: "\u{1F60A}", check: "noSleep" },
  { id: "noCatch", emoji: "\u{1F977}", check: "noCatch" },
  { id: "minigame", emoji: "\u{1F3AE}", check: "minigameWon" },
  { id: "monologue", emoji: "\u{1F3A4}", check: "monologue" },
  { id: "invisible", emoji: "\u{1F47B}", check: "invisible" },
  { id: "question", emoji: "\u{1F4A1}", check: "questionCorrect" },
  { id: "proc3x", emoji: "\u{1F60E}", check: "procrastinated3x" },
  { id: "dirChange", emoji: "\u{1F500}", check: "directionChange" },
];

export interface BingoCard {
  goals: BingoGoalSpec[];   // 4 random goals; the wording comes from the locale
  completed: boolean[];     // which goals are done
}

// ---- Weekend Events ----

export enum WeekendEventType {
  TeamBuilding = "Team-Building",
  Betriebsfeier = "Betriebsfeier",
  Sommerfest = "Sommerfest",
  Weihnachtsfeier = "Weihnachtsfeier",
  HackDay = "Hack Day",
}

export interface WeekendEventDef {
  type: WeekendEventType;
  name: string;
  emoji: string;
  description: string;
  meetingTypes: MeetingType[];
  bonusSalary: number;
  bonusFatigueReduction: number;
}

export interface WeekendEventSpec {
  type: WeekendEventType;
  emoji: string;
  meetingTypes: MeetingType[];
  bonusSalary: number;
  bonusFatigueReduction: number;
}

export const WEEKEND_EVENT_SPECS: WeekendEventSpec[] = [
  {
    type: WeekendEventType.TeamBuilding,
    emoji: "\u{1F3D5}\uFE0F",
    meetingTypes: [MeetingType.Brainstorming, MeetingType.Retro, MeetingType.Schulung],
    bonusSalary: 100,
    bonusFatigueReduction: 20,
  },
  {
    type: WeekendEventType.Betriebsfeier,
    emoji: "\u{1F389}",
    meetingTypes: [MeetingType.AllHands, MeetingType.LunchMeeting],
    bonusSalary: 50,
    bonusFatigueReduction: 30,
  },
  {
    type: WeekendEventType.Sommerfest,
    emoji: "\u2600\uFE0F",
    meetingTypes: [MeetingType.StatusUpdate, MeetingType.LunchMeeting, MeetingType.Brainstorming],
    bonusSalary: 75,
    bonusFatigueReduction: 25,
  },
  {
    type: WeekendEventType.Weihnachtsfeier,
    emoji: "\u{1F384}",
    meetingTypes: [MeetingType.AllHands, MeetingType.OneOnOne, MeetingType.LunchMeeting],
    bonusSalary: 150,
    bonusFatigueReduction: 15,
  },
  {
    type: WeekendEventType.HackDay,
    emoji: "\u{1F4BB}",
    meetingTypes: [MeetingType.Planning, MeetingType.Scrum, MeetingType.Brainstorming, MeetingType.StatusUpdate],
    bonusSalary: 200,
    bonusFatigueReduction: 10,
  },
];

// ---- Multiple Endings ----

export enum EndingType {
  Fired = "fired",
  Burnout = "burnout",
  Retired = "retired",
  Promoted = "promoted",
  Whistleblower = "whistleblower",
}

export interface GameEnding {
  type: EndingType;
  title: string;
  emoji: string;
  description: string;
  bonusPoints: number;
}

export interface EndingSpec {
  type: EndingType;
  emoji: string;
  bonusPoints: number;
}

export const ENDING_SPECS: Record<EndingType, EndingSpec> = {
  [EndingType.Fired]: { type: EndingType.Fired, emoji: "\u{1F480}", bonusPoints: 0 },
  [EndingType.Burnout]: { type: EndingType.Burnout, emoji: "\u{1F525}", bonusPoints: 1 },
  [EndingType.Retired]: { type: EndingType.Retired, emoji: "\u{1F3D6}\uFE0F", bonusPoints: 5 },
  [EndingType.Promoted]: { type: EndingType.Promoted, emoji: "\u{1F451}", bonusPoints: 10 },
  [EndingType.Whistleblower]: { type: EndingType.Whistleblower, emoji: "\u{1F4E2}", bonusPoints: 8 },
};

// ---- Extended Meta-Progression (Permanent Unlocks) ----

export enum PermanentUnlockId {
  Firmenwagen = "firmenwagen",
  BueroHund = "bueroHund",
  KantineFlatrate = "kantineFlatrate",
  GoldeneBadge = "goldeneBadge",
  Homeoffice = "homeoffice",
  Dienstwohnung = "dienstwohnung",
}

export interface PermanentUnlockDef {
  id: PermanentUnlockId;
  name: string;
  emoji: string;
  description: string;
  cost: number;         // total Impostor Points required to unlock
  effect: string;       // description of the effect
}

export interface PermanentUnlockSpec {
  id: PermanentUnlockId;
  emoji: string;
  cost: number;         // total Impostor Points required to unlock
}

export const PERMANENT_UNLOCK_SPECS: PermanentUnlockSpec[] = [
  { id: PermanentUnlockId.Firmenwagen, emoji: "\u{1F697}", cost: 15 },
  { id: PermanentUnlockId.BueroHund, emoji: "\u{1F415}", cost: 25 },
  { id: PermanentUnlockId.KantineFlatrate, emoji: "\u{1F37D}\uFE0F", cost: 40 },
  { id: PermanentUnlockId.GoldeneBadge, emoji: "\u{1F3C5}", cost: 60 },
  { id: PermanentUnlockId.Homeoffice, emoji: "\u{1F3E0}", cost: 80 },
  { id: PermanentUnlockId.Dienstwohnung, emoji: "\u{1F3E2}", cost: 100 },
];

// ---- Positive Random Events (between meetings) ----

export interface PositiveEvent {
  id: string;
  title: string;
  description: string;
  fatigueBonus?: number;
  salaryBonus?: number;
  skipMeeting?: boolean;
}

export interface PositiveEventSpec {
  id: string;
  fatigueBonus?: number;
  salaryBonus?: number;
  skipMeeting?: boolean;
}

export const POSITIVE_EVENT_SPECS: PositiveEventSpec[] = [
  { id: "freeCoffee", fatigueBonus: 15 },
  { id: "donuts", fatigueBonus: 10 },
  { id: "fireAlarm", skipMeeting: true },
  { id: "overtimeBonus", salaryBonus: 100 },
  { id: "meetingCancelled", skipMeeting: true },
  { id: "mindfulness", fatigueBonus: 20 },
  { id: "birthdayCake", fatigueBonus: 8, salaryBonus: 0 },
  { id: "praise", salaryBonus: 50 },
];

// ---- Sound Effects ----

export enum SoundEffect {
  Addressed = "addressed",
  Caught = "caught",
  FellAsleep = "fellAsleep",
  GameOver = "gameOver",
  Achievement = "achievement",
  PowerUp = "powerUp",
  MinigameWin = "minigameWin",
  MinigameFail = "minigameFail",
  VendorAppear = "vendorAppear",
  BingoComplete = "bingoComplete",
}
